import { Link, useLocation } from 'react-router-dom'
import { Alert, Modal } from 'components'
import images from 'images'
import s from './styles.module.scss'
import { useEffect, useRef, useState } from 'react'
import * as api from 'api'
import { getAuthUserData } from 'utils/authManager'
import { usePushNotifications } from '../../../hooks/usePushNotifications'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST

export default function Main({ fullscreen, setFullscreen }) {
  const [isScrolled, setIsScrolled] = useState(false)
  const userData = getAuthUserData()
  const location = useLocation()
  const { supported, subscribed, requestPermission, unsubscribe, refresh, loading } = usePushNotifications()

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div id='nav' className={isScrolled ? s.outerNav + ' ' + s.outerNavScrolled : s.outerNav}>
      <nav className={isScrolled ? s.nav + ' ' + s.scrolled : s.nav}>
        <div className={s.left}>
          <Link to='/home'>
            <img src={images.logo} alt='' />
          </Link>
        </div>

        <div className={s.right}>
          <NavLinks isScrolled={isScrolled} />
        </div>


        <div className={s.mobileLinks}>
          {!userData && (
            <Link to='/signUp' className={s.highlight}>
              Register Now
            </Link>
          )}
          {!userData && (
            <Link to='/login' state={location}>
              Login
            </Link>
          )}

          <div className={s.mapIcon} onClick={() => Modal.LocationsMap()} title="View Locations Map">
            <img src={images.googleMapsIcon} alt="Map" />
          </div>

          {/* Debug: Log why notification icon might not be showing */}
          {typeof window !== 'undefined' && window.console && (
            (() => {
              if (!userData) {
                console.log('[Navbar] Notification icon hidden: User not logged in')
              } else if (!supported) {
                const checks = {
                  serviceWorker: 'serviceWorker' in navigator,
                  pushManager: 'PushManager' in window,
                  notification: 'Notification' in window,
                  userAgent: navigator.userAgent,
                  isWebView: navigator.userAgent.includes('wv') || navigator.userAgent.includes('BCNIndiaApp')
                }
                console.warn('[Navbar] Notification icon hidden: Push notifications not supported', checks)
                console.warn('[Navbar] To fix:', {
                  ifNoServiceWorker: 'Update Android System WebView from Play Store',
                  ifNoNotification: 'Check Android notification permissions in Settings',
                  ifWebView: 'Ensure WebView Chrome version is 40+ (check in Play Store)'
                })
              } else {
                console.log('[Navbar] ✅ Notification icon should be visible', { subscribed, loading, supported })
              }
              return null
            })()
          )}

          {!!userData && supported && (
            <>
              {!subscribed && (
                <div
                  className={s.notificationIcon}
                  onClick={async () => {
                    const result = await requestPermission()
                    if (result && result.success) {
                      Alert.success('Notifications enabled!')
                    } else if (result && !result.success) {
                      Alert.error(result.error || 'Failed to enable notifications')
                    }
                  }}
                  title="Enable push notifications"
                >
                  <span className='material-icons'>notifications_off</span>
                </div>
              )}
              {subscribed && (
                <div
                  className={s.notificationIcon + ' ' + s.notificationEnabled}
                  onClick={() => {
                    Modal.Confirm('Do you want to disable push notifications?', async () => {
                      try {
                        const result = await unsubscribe()
                        if (result && result.success) {
                          Alert.success('Notifications disabled')
                          if (refresh) {
                            setTimeout(() => {
                              refresh()
                            }, 300)
                          }
                        } else {
                          Alert.error(result?.error || 'Failed to disable notifications')
                        }
                      } catch (error) {
                        Alert.error('Failed to disable notifications: ' + error.message)
                      }
                    }, true)
                  }}
                  title="Click to disable notifications"
                >
                  <span className='material-icons'>notifications</span>
                </div>
              )}
            </>
          )}

          <div className={s.hamburger + ' material-icons'} onClick={() => setFullscreen(true)}>
            menu
          </div>
        </div>
      </nav>

      {!!fullscreen && (
        <div
          className={s.fullscreenMenu}
          onClick={() => {
            setFullscreen(false)
          }}
        >
          <div className={s.close + ' material-icons'}>close</div>
          <NavLinks fullScreen />
        </div>
      )}
    </div>
  )
}

const NavLinks = props => {
  const userData = getAuthUserData()
  const processing = useRef()
  const [loading, setLoading] = useState(false)
  const location = useLocation()
  const { supported, subscribed, requestPermission, unsubscribe, refresh } = usePushNotifications()

  const logOutHandler = async () => {
    Modal.Confirm(
      'Are you sure you want to logout?',
      async () => {
        if (processing.current) return
        processing.current = true
        setLoading(true)
        Alert.success('Logging Out...')

        const logOut = await api.auth.user.logOut({})

        if (
          logOut.code === 201 ||
          logOut.message === 'jwt expired' ||
          logOut.message === 'UnAuthorized - Authorization required!'
        ) {
          window.localStorage.removeItem('authorization')
          window.localStorage.removeItem('userData')
          window.localStorage.removeItem('planStatusShownDate')
          Alert.success('Thanks for visting BCN, Would be happy to serve you again!')
          window.location.reload()
        } else Alert.error(logOut.message)

        setLoading(false)
        processing.current = false
      },
      true
    )
  }

  return (
    <div className={s.navLinks}>
      {navLinks.map(({ name, path, showWhen }, i) => {
        // Handle special cases for visibility
        if (path === '/signUp') {
          return !userData && !props.fullScreen ? (
            <Link key={i} to={path} className={window.location.pathname.startsWith(path) ? s.active : s.highlight}>
              <div>{name}</div>
            </Link>
          ) : null
        }
        
        if (path === '/userAdmin/businesses') {
          return userData ? (
            <Link
              key={i}
              to={path}
              className={window.location.pathname.startsWith(path) ? s.active + ' ' + s.highlight : ' ' + s.highlight}
            >
              <div>{name}</div>
            </Link>
          ) : null
        }
        
        if (showWhen === 'notLoggedIn') {
          return !userData ? (
            <Link key={i} to={path} className={window.location.pathname.startsWith(path) ? s.active : ''}>
              <div>{name}</div>
            </Link>
          ) : null
        }
        
        // Default case - show all other links
        return (
          <Link key={i} to={path} className={window.location.pathname.startsWith(path) ? s.active : ''}>
            <div>{name}</div>
          </Link>
        )
      })}

      {/* {!!userData && (
        <Link to='/userAdmin/businesses' className={s.responsiveLinks + ' ' + s.highlight}>
          My Businesses
        </Link>
      )} */}
      {!!userData && (
        <Link to='/userAdmin/editProfile' className={s.responsiveLinks}>
          Edit Profile
        </Link>
      )}
      {!!userData && (
        <Link to='/userAdmin/memberShipCard' className={s.responsiveLinks}>
          Membership
        </Link>
      )}
      {!!userData && (
        <Link to='/userAdmin/notifications' className={s.responsiveLinks}>
          Notifications
        </Link>
      )}
      {!!userData && (
        <div className={s.responsiveLinks + ' ' + s.logOut} onClick={loading ? () => {} : logOutHandler}>
          Logout
        </div>
      )}
      {!userData && (
        <Link to='/faq' className={s.responsiveLinks}>
          Help
        </Link>
      )}
      {!userData && !props.fullScreen && (
        <Link to='/login' state={location} className={s.responsiveLinks}>
          Login
        </Link>
      )}

      {!!userData && supported && (
        <>
          {!subscribed && (
            <div
              className={s.notificationIcon}
              onClick={async () => {
                const result = await requestPermission()
                if (result && result.success) {
                  Alert.success('Notifications enabled!')
                } else if (result && !result.success) {
                  Alert.error(result.error || 'Failed to enable notifications')
                }
              }}
              title="Enable push notifications"
            >
              <span className='material-icons'>notifications_off</span>
            </div>
          )}
          {subscribed && (
            <div
              className={s.notificationIcon + ' ' + s.notificationEnabled}
              onClick={(e) => {
                Modal.Confirm('Do you want to disable push notifications?', async () => {
                  try {
                    const result = await unsubscribe()
                    if (result && result.success) {
                      Alert.success('Notifications disabled')
                      if (refresh) {
                        setTimeout(() => {
                          refresh()
                        }, 300)
                      }
                    } else {
                      Alert.error(result?.error || 'Failed to disable notifications')
                    }
                  } catch (error) {
                    Alert.error('Failed to disable notifications: ' + error.message)
                  }
                }, true)
              }}
              title="Click to disable notifications"
            >
              <span className='material-icons'>notifications</span>
            </div>
          )}
        </>
      )}

      <div className={s.user}>
        <div className={s.logo} title={!!userData ? userData.name : ''}>
          <img src={!!userData && userData.logo ? IMAGE_HOST + userData.logo : images.Profile} alt='' />
        </div>

        <div className={s.hoverLinks}>
          <div className={s.hoverInner} style={{ marginTop: props.isScrolled ? '2.5rem' : '3rem' }}>
            {/* {!!userData && (
              <Link to='/userAdmin/businesses' className={s.highlight}>
                My Businesses
              </Link>
            )} */}
            {!!userData && <Link to='/userAdmin/editProfile'>Edit Profile</Link>}
            {!!userData && <Link to='/userAdmin/memberShipCard'>Membership</Link>}
            {!!userData && <Link to='/userAdmin/notifications'>Notifications</Link>}
            {!!userData && (
              <div className={s.login} onClick={loading ? () => {} : logOutHandler}>
                Logout
              </div>
            )}
            {!userData && <Link to='/faq'>Help</Link>}
            {!userData && (
              <Link to='/login' state={location} className={s.login}>
                Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
const navLinks = [
  {
    name: 'Home',
    path: '/home',
    icon: 'home'
  },
  {
    name: 'Register Now',
    path: '/signUp',
    icon: 'app_registration'
  },
  {
    name: 'Business Connect',
    path: '/home/businessConnect',
    icon: 'business_center',
    showWhen: 'notLoggedIn'
  },
  {
    name: 'My Business',
    path: '/userAdmin/businesses',
    icon: 'business'
  },
  {
    name: 'Spirituality',
    path: '/spirituality',
    icon: 'spirituality'
  },
  {
    name: 'Jobs',
    path: '/jobs',
    icon: 'work'
  },
  {
    name: 'Events',
    path: '/events',
    icon: 'event'
  },
  {
    name: 'Matrimonial',
    path: '/matrimonial',
    icon: 'diversity_1'
  },
  {
    name: 'FAQ',
    path: '/faq',
    icon: 'help'
  },
  {
    name: 'About Us',
    path: '/aboutUs',
    icon: 'description'
  },
  {
    name: 'Contact Us',
    path: '/contactUs',
    icon: 'contact_support'
  }
]

